import React, { useEffect, useState } from "react";
import { ICashTransferClient } from "../../types/cashTransferClient.type";
import { api } from "../../api/api";
import {
  Button,
  Dropdown,
  Form,
  Input,
  Menu,
  message,
  Modal,
  Popconfirm,
  Space,
  Table,
  Typography,
} from "antd";
import { useNavigate } from "react-router-dom";
import { DownOutlined } from "@ant-design/icons";
import { roundToThreeDecimals } from "../../utils/roundToThreeDecimals";
import { useDownloadFile } from "../../hooks/useDownloadFile";
import { PiMicrosoftExcelLogo } from "react-icons/pi";
import { useAuth } from "../../contexts/auth.context";
import { UserRole } from "../../types/user.type";

const { Title } = Typography;
const { Search } = Input;

export default function CashTransferClients() {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const [clients, setClients] = useState<ICashTransferClient[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [form] = Form.useForm();
  const { loading: downloadLoading, downloadFile } = useDownloadFile();

  useEffect(() => {
    fetchClients();
  }, [page, pageSize, searchTerm]);

  const handleExport = () => {
    downloadFile("/export/cash-transfer/clients", "clients_data.xlsx");
  };

  const fetchClients = async () => {
    setLoading(true);

    try {
      const response = await api.get("/cash-transfer/clients", {
        params: {
          page,
          limit: pageSize,
          search: searchTerm,
        },
      });

      setClients(response.data.clients);
      setTotal(response.data.total);
    } catch (error) {
      console.error("Error fetching clients", error);
      message.error("Ошибка при получение клиентов.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitForm = async (values: { name: string }) => {
    setConfirmLoading(true);

    try {
      await api.post("/cash-transfer/client", values);
      message.success("Клиент успешно добавлен.");

      fetchClients();
      setIsModalOpen(false);
      setConfirmLoading(false);
    } catch (error: any) {
      setConfirmLoading(false);
      console.error(error);
      message.error(error.response.data.error);
    }
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setPage(1);
  };

  const handleDelete = async (id: string) => {
    try {
      await api.delete(`/cash-transfer/client/${id}`);

      message.success("Клиент успешно удален.");
      fetchClients();
    } catch (error: any) {
      message.error(error.response.data.error);
    }
  };

  const columns = [
    {
      title: "Имя",
      dataIndex: "name",
      key: "name",
      fixed: "left" as "left",
    },
    {
      title: "Долг клиента в USD",
      key: "clientDebtUSD",
      render: (_: any, client: ICashTransferClient) =>
        roundToThreeDecimals(client.totalClientDebt.USD),
    },
    {
      title: "Долг клиента в UZS",
      key: "clientDebtUSD",
      render: (_: any, client: ICashTransferClient) =>
        roundToThreeDecimals(client.totalClientDebt.UZS),
    },
    {
      title: "Наш долг в USD",
      key: "clientDebtUSD",
      render: (_: any, client: ICashTransferClient) =>
        roundToThreeDecimals(client.totalCompanyDebt.USD),
    },
    {
      title: "Наш долг в UZS",
      key: "clientDebtUSD",
      render: (_: any, client: ICashTransferClient) =>
        roundToThreeDecimals(client.totalCompanyDebt.UZS),
    },
    {
      title: "Создал",
      dataIndex: "ownerName",
      key: "ownerName",
    },
    {
      title: "",
      key: "actions",
      render: (_: any, client: ICashTransferClient) => {
        const menu = (
          <Menu>
            {authState.user?.role === UserRole.Admin && (
              <Menu.Item>
                <Popconfirm
                  title="Вы уверены, что хотите удалить этого клиента?"
                  onConfirm={() => handleDelete(client._id)}
                  okText="Да"
                  cancelText="Нет"
                >
                  Удалить
                </Popconfirm>
              </Menu.Item>
            )}
            <Menu.Item
              onClick={() =>
                navigate(`/cash-transfers/${client._id}/${client.name}`)
              }
            >
              Смотреть Конвертации
            </Menu.Item>
          </Menu>
        );

        return (
          <Dropdown overlay={menu}>
            <Button type="link">
              Действия <DownOutlined />
            </Button>
          </Dropdown>
        );
      },
    },
  ];
  return (
    <div style={{ padding: 24 }}>
      <Title>Клиенты Обнал</Title>
      <Space style={{ marginBottom: 16 }}>
        <Search
          placeholder="Поиск по названию"
          onSearch={handleSearch}
          allowClear
        />
        <Button type="primary" onClick={() => setIsModalOpen(true)}>
          Добавить
        </Button>
        <Button
          type="default"
          loading={downloadLoading}
          icon={<PiMicrosoftExcelLogo />}
          onClick={handleExport}
        >
          Экспорт в Excel
        </Button>
      </Space>

      <Table
        columns={columns}
        dataSource={clients}
        rowKey={(record) => record._id}
        loading={loading}
        pagination={{
          current: page,
          total: total,
          pageSize: pageSize,
          onChange: (page, size) => {
            setPage(page);
            if (size !== pageSize) setPageSize(size!);
          },
          showSizeChanger: true,
        }}
        scroll={{ x: "max-content" }}
      />

      <Modal
        title="Добавить клиента"
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={confirmLoading}
      >
        <Form form={form} onFinish={handleSubmitForm} layout="vertical">
          <Form.Item
            name="name"
            label="Имя"
            rules={[{ required: true, message: "Введите имя" }]}
          >
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
